/**
 * Canonical product information layer.
 *
 * One source of truth per SKU: `src/data/product-facts.generated.ts`, built by
 * `bun scripts/build-product-facts.ts` from the merchant's own Shopify content.
 *
 * Rule: only verified facts are shown to customers. If a field is missing from
 * the canonical record it is omitted from the UI (and listed in
 * docs/tuotetiedot-puutteet.md) — it is never guessed, defaulted or invented.
 */
import { PRODUCT_FACTS } from "@/data/product-facts.generated";
import { guardClaims } from "@/lib/claim-guard";

export type ProductFacts = {
  recommendedAge?: string;
  /** Manufacturer age window in months, derived from `recommendedAge` only. */
  ageMinMonths?: number;
  ageMaxMonths?: number;
  /** Benefit/feature bullets taken verbatim from the manufacturer's own copy. */
  featureList?: string[];

  dimensions?: string;
  foldedDimensions?: string;
  weight?: string;
  maxLoad?: string;
  material?: string;
  surfaceTreatment?: string;
  includes?: string;
  assembly?: string;
  care?: string;
  warranty?: string;
  safety?: string;
  features?: string;
  ecology?: string;
  certifications?: string;
  colors?: string;
  usage?: string;
  note?: string;
  /** Certifications/standards detected in the product's own content. */
  standards?: string[];
  /** Only set when the product content states the country explicitly. */
  manufacturingCountry?: string;
  /** Fallback when only "valmistettu Euroopassa" is stated. */
  manufacturingRegion?: string;
  /** Shopify vendor = brand / manufacturer. */
  manufacturer?: string;
  /** Verified dispatch origin for this SKU, e.g. "Suomi", "Latvia", "EU". */
  shippingOrigin?: string;
  /** Verified manufacturer warranty length in months. */
  warrantyMonths?: number;
  /** Only true when the product's own documentation states CE marking. */
  ceMarked?: boolean;
  /** Only set when the product content states it explicitly. */
  designCountry?: string;
  /** Estimated assembly time, when the manufacturer states one. */
  assemblyTime?: string;
  /** "sisä" / "ulko" / "sisä ja ulko" — only when documented. */
  indoorOutdoor?: string;
  /** Explicit adult-supervision requirement from the manufacturer. */
  adultSupervision?: string;
};

export type SpecRow = { label: string; value: string };

/**
 * Store-level facts that apply to every order (verified retailer policy).
 * Nothing here may describe a warehouse location or dispatch speed that is
 * not true for every SKU — per-SKU dispatch lives in `facts.shippingOrigin`.
 */
export const STORE_FACTS = {
  retailer: "Montessori24.fi — suomalainen verkkokauppa",
  dispatch: "Varastossa olevat tuotteet pyritään lähettämään 1–2 arkipäivän kuluessa",
  deliveryTime: "3–7 arkipäivää Suomessa",
  deliveryPrice: "Ilmainen toimitus Suomeen",
  returns: "30 päivän palautusetu (lakisääteinen peruutusoikeus 14 pv)",
  vat: "Hinnat sisältävät ALV 25,5 %",
  support: "asiakaspalvelu@montessori24.fi",
} as const;

export function getProductFacts(handle?: string): ProductFacts {
  if (!handle) return {};
  return (PRODUCT_FACTS as Record<string, ProductFacts>)[handle] ?? {};
}

const clean = (v?: string) => (v ? v.replace(/\s*\n\s*/g, " · ").trim() : undefined);

/** "Tuotetiedot" rows — verified values only. */
export function specRows(f: ProductFacts): SpecRow[] {
  const rows: Array<[string, string | undefined]> = [
    ["Ikäsuositus", clean(f.recommendedAge)],
    ["Mitat", clean(f.dimensions)],
    ["Mitat taitettuna", clean(f.foldedDimensions)],
    ["Paino", clean(f.weight)],
    ["Enimmäiskuormitus", clean(f.maxLoad)],
    ["Materiaali", clean(f.material)],
    ["Pintakäsittely", clean(f.surfaceTreatment)],
    ["Värit", clean(f.colors)],
    ["Pakkauksen sisältö", clean(f.includes)],
    ["Kokoaminen", clean(f.assembly)],
    ["Arvioitu kokoamisaika", clean(f.assemblyTime)],
    ["Käyttöympäristö", clean(f.indoorOutdoor)],
    ["Takuu", f.warrantyMonths ? `${f.warrantyMonths} kk valmistajan takuu` : clean(f.warranty)],
    ["Valmistaja", f.manufacturer],
    ["Valmistusmaa", f.manufacturingCountry ?? f.manufacturingRegion],
    ["Suunnittelumaa", f.designCountry],
    ["Myyjä", "Montessori24.fi, Suomi"],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1])).map(([label, value]) => ({ label, value }));
}

/** "Turvallisuus" rows — only standards the product itself documents. */
export function safetyRows(f: ProductFacts): SpecRow[] {
  const rows: Array<[string, string | undefined]> = [
    ["CE-merkintä", isCeMarked(f) ? "CE-merkitty" : undefined],
    ["Standardit", f.standards?.length ? f.standards.join(" · ") : clean(f.certifications)],
    ["Aikuisen valvonta", clean(f.adultSupervision)],
    ["Turvallisuustiedot", guardClaims(clean(f.safety)) || undefined],
    ["Enimmäiskuormitus", clean(f.maxLoad)],
    ["Ikäsuositus", clean(f.recommendedAge)],
    ["Huomioi", clean(f.note)],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1])).map(([label, value]) => ({ label, value }));
}

/**
 * Delivery rows. The dispatch-origin row is only rendered for SKUs whose
 * fulfilment location is recorded — we never claim a Finnish warehouse for
 * items that may ship from the manufacturer.
 */
export function deliveryRows(f: ProductFacts = {}): SpecRow[] {
  const rows: Array<[string, string | undefined]> = [
    ["Lähetys", f.shippingOrigin ? shippingOriginLabel(f.shippingOrigin) : undefined],
    ["Käsittelyaika", STORE_FACTS.dispatch],
    ["Toimitusaika", STORE_FACTS.deliveryTime],
    ["Toimituskulut", STORE_FACTS.deliveryPrice],
    ["Palautus", STORE_FACTS.returns],
    ["Hinnat", STORE_FACTS.vat],
    ["Asiakaspalvelu", STORE_FACTS.support],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1])).map(([label, value]) => ({ label, value }));
}

/** Customer-facing wording for a verified dispatch origin. */
export function shippingOriginLabel(origin: string): string {
  const key = origin.trim().toLowerCase();
  if (key === "suomi" || key === "finland") return "Suomen varastosta";
  if (key === "eu") return "EU-varastosta";
  if (key.includes("valmistaj")) return "Valmistajan varastosta";
  return `Varastosta: ${origin}`;
}

/**
 * GPSR (EU 2023/988) economic-operator and safety rows.
 *
 * The regulation requires the manufacturer and the EU-responsible operator to
 * be identifiable on the product page, together with any warnings. Only
 * verified values are shown; where the manufacturer's own address is not on
 * file we say so instead of inventing one.
 */
/** True when the product's own documentation or standards state CE marking. */
export function isCeMarked(f: ProductFacts): boolean {
  return Boolean(f.ceMarked || f.standards?.some((s) => /\bCE\b/i.test(s)) || /\bCE\b/i.test(f.certifications ?? ""));
}

export function gpsrRows(f: ProductFacts): SpecRow[] {
  const rows: Array<[string, string | undefined]> = [
    // GPSR requires the manufacturer to be identifiable. When the vendor field
    // is not a real manufacturer we say so instead of omitting the row.
    ["Valmistaja", f.manufacturer ?? "Ei vahvistettu tuotetiedoissa — toimitamme valmistajan tiedot pyynnöstä"],
    ["Valmistusmaa", f.manufacturingCountry ?? f.manufacturingRegion],
    [
      "EU-vastuuhenkilö",
      "MLR Solutions Oy (Montessori24.fi), Y-tunnus 3582909-7, Suomi · asiakaspalvelu@montessori24.fi",
    ],
    ["CE-merkintä", isCeMarked(f) ? "CE-merkitty" : undefined],
    ["Standardit", f.standards?.length ? f.standards.join(" · ") : clean(f.certifications)],
    [
      "Varoitukset",
      guardClaims(clean(f.safety) ?? clean(f.note)) ||
        "Kokoa valmistajan ohjeen mukaan, tarkista kiinnitykset säännöllisesti ja valvo pientä lasta leikin aikana.",
    ],
    ["Ikäsuositus", clean(f.recommendedAge)],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1])).map(([label, value]) => ({ label, value }));
}

export function careRows(f: ProductFacts): SpecRow[] {
  const rows: Array<[string, string | undefined]> = [
    ["Hoito-ohje", clean(f.care)],
    ["Pintakäsittely", clean(f.surfaceTreatment)],
    ["Materiaali", clean(f.material)],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1])).map(([label, value]) => ({ label, value }));
}

/** One-line origin statement, always honest about what is known. */
export function originLine(f: ProductFacts): string {
  const made = f.manufacturingCountry
    ? `valmistettu maassa ${f.manufacturingCountry}`
    : f.manufacturingRegion
      ? "valmistettu Euroopassa"
      : null;
  const brand = f.manufacturer ? `Valmistaja ${f.manufacturer}` : "Eurooppalaisen valmistajan tuote";
  return made
    ? `${brand}, ${made}. Myyjä Montessori24.fi (MLR Solutions Oy), Suomi.`
    : `${brand}. Myyjä Montessori24.fi (MLR Solutions Oy), Suomi.`;
}

/**
 * Internal data-quality signal. Rendered only in development so the merchant
 * can see which verified fields a SKU is still missing — never shown to
 * customers, and never used to fill a value in.
 */
const IMPORTANT_FIELDS: Array<[keyof ProductFacts, string]> = [
  ["manufacturer", "valmistaja"],
  ["manufacturingCountry", "valmistusmaa"],
  ["recommendedAge", "ikäsuositus"],
  ["dimensions", "mitat"],
  ["weight", "tuotteen paino"],
  ["maxLoad", "maksimikuormitus"],
  ["material", "materiaalit"],
  ["warrantyMonths", "takuu"],
  ["shippingOrigin", "lähetyksen alkuperä"],
  ["safety", "turvallisuustiedot"],
];

export function factsCompleteness(f: ProductFacts): { percent: number; missing: string[] } {
  const missing = IMPORTANT_FIELDS.filter(([k]) => !f[k]).map(([, label]) => label);
  const percent = Math.round(((IMPORTANT_FIELDS.length - missing.length) / IMPORTANT_FIELDS.length) * 100);
  return { percent, missing };
}

/** Age string for badges — undefined when not verified. */
export function verifiedAge(f: ProductFacts): string | undefined {
  return f.recommendedAge;
}

/**
 * Age-bucket membership, derived only from the manufacturer's own age
 * recommendation. A product with no verified age is never placed in an age
 * bucket — browsing by age must not rely on keyword guesses from the title.
 */
/**
 * Products that only become usable once a child stands, climbs or walks.
 * Manufacturers often state a "6 kk +" window because a baby may look at or
 * touch the frame, but merchandising these under "Ikä 0–1 v" misleads parents
 * shopping for an infant.
 */
const REQUIRES_STANDING =
  /(climb|climber|triangle|arch|ramp|tower|stool|rack|shelf|bed|beam|ladder|slide|kolmio|kiipeil|torni|rekki|hylly|s(a|ä)nky|puomi|jakkara|liukum)/i;

export function matchesAgeBucket(handle: string, bucketMinMonths: number, bucketMaxMonths: number): boolean {
  const f = getProductFacts(handle);
  if (f.ageMinMonths == null) return false;
  if (bucketMaxMonths <= 12 && REQUIRES_STANDING.test(handle)) return false;
  const max = f.ageMaxMonths ?? Number.POSITIVE_INFINITY;
  return f.ageMinMonths < bucketMaxMonths && max > bucketMinMonths;
}

/**
 * Floor-level baby products (soft play, mats, mirrors, rattles, sensory toys).
 * These need no standing or climbing, so they are suitable from birth even when
 * the manufacturer has not published an explicit age window. Used to keep the
 * "Ikä 0–1 v" shop filter consistent with the /montessori-vauva landing page.
 */
const BABY_FLOOR_PLAY =
  /(iglu|pehmo|pehme|pallomeri|ball ?pit|matto|matt|patja|mattress|kiila|wedge|helistin|rattle|peili|mirror|sensor|tarttum|purulelu|teether|mobile|helmi|bead|sateenkaari|rainbow)/i;

/** True when a product belongs in the 0–1 v browse bucket. */
export function matchesBabyBucket(handle: string, title = "", vendor = ""): boolean {
  if (matchesAgeBucket(handle, 0, 12)) return true;
  if (REQUIRES_STANDING.test(handle)) return false;
  return BABY_FLOOR_PLAY.test(title) || BABY_FLOOR_PLAY.test(vendor) || BABY_FLOOR_PLAY.test(handle);
}


/**
 * Shopify vendor values that are storefront/collection labels rather than a
 * real manufacturer. Showing them as a brand misdescribes the product.
 */
const NON_MANUFACTURER_VENDORS = /^(montessori house bed|default|montessori24)$/i;

/** Verified brand name, or undefined when the vendor field is not a real brand. */
export function displayBrand(vendor?: string): string | undefined {
  const v = vendor?.trim();
  return v && !NON_MANUFACTURER_VENDORS.test(v) ? v : undefined;
}
